import React, { useState, useEffect, useCallback } from "react";
import LoadingScreen from "../common/LoadingScreen";
import ErrorScreen from "../common/ErrorScreen";
import Image from "../common/Image";
import api from "../../hooks/api";
import { API_URLS } from "../../App";

interface Product {
  id: string;
  name: string;
  description: string;
  highlights: string[];
  type: string;
  image?: string;
  stock: number;
  category: string;
  rootCategory: string;
  features: Feature[];
}

interface Feature {
  name: string;
  value: string;
  order: number;
}

const ProductDetails = ({ reference }: { reference: string }) => {
  const [componentDetails, setComponentDetails] = useState<
    Record<string, Product | null>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    reference,
  );

  const fetchComponentDetails = useCallback(
    async (id: string): Promise<Product | null> => {
      try {
        const response = await api.get(API_URLS.PRODUCTS + `/${id}`);
        return response.data;
      } catch (error) {
        if (error instanceof Error) {
          // @ts-ignore
          if (error.status === 404) {
            console.warn(`Component with ID ${id} not found.`);
            return null;
          }
          setError(error.message);
        } else {
          setError("An unexpected error occurred");
        }
        console.warn(`Error fetching component details for ID ${id}:`, error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchComponentDetails(reference).then((component) => {
      setComponentDetails((prevDetails) => ({
        ...prevDetails,
        [reference]: component,
      }));
      setLoading(false);
    });
  }, [fetchComponentDetails, reference]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen message={error} />;
  }

  // @ts-ignore
  return (
    <div className="components-details-view flex flex-col md:flex-row gap-4">
      <div
        id="component-detail"
        className="component-detail content w-full p-5 bg-white rounded-lg shadow-md"
      >
        {selectedComponent && componentDetails[selectedComponent] ? (
          <>
            <h3 className="component-title font-extrabold text-3xl mb-4">
              {componentDetails[selectedComponent]?.name || "Component Details"}
            </h3>
            <div className="flex flex-col md:flex-row gap-4">
              {componentDetails[selectedComponent]?.image && (
                <div className="component-image mb-4 w-full md:w-1/2">
                  <Image
                    src={componentDetails[selectedComponent]?.image}
                    alt={
                      componentDetails[selectedComponent]?.name ||
                      "Component Image"
                    }
                    sizes="400"
                    className="max-w-full h-auto rounded-md"
                  />
                </div>
              )}
              <div className="component-highlights p-3 w-full md:w-1/2">
                <ul className="list-disc list-inside">
                  {componentDetails[selectedComponent]?.highlights.map(
                    (highlight, index) => (
                      <li key={index}>{highlight}</li>
                    ),
                  )}
                </ul>
              </div>
            </div>
            <div className="component-description mt-4">
              {componentDetails[selectedComponent]?.description ||
                "No description available."}
              {componentDetails[selectedComponent]?.features && (
                <ul className="mt-4">
                  {componentDetails[selectedComponent]?.features.map(
                    (feature, index) => (
                      <li key={index}>
                        <strong className="font-semibold">
                          {feature.name}:
                        </strong>{" "}
                        {feature.value}
                      </li>
                    ),
                  )}
                </ul>
              )}
              <p className="mt-2">
                <strong className="font-semibold">Stock:</strong>{" "}
                {componentDetails[selectedComponent]?.stock || "N/A"}
              </p>
            </div>
          </>
        ) : (
          <h3 className="component-title font-extrabold text-3xl mb-4">
            Não foi possível encontrar o componente selecionado.
          </h3>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;
